import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, Redirect } from 'react-router-dom';
import { Subscribe } from 'unstated';
import OrganizationContainer from '../../../../shared/state/containers/OrganizationContainer';
import UserContainer from '../../../../shared/state/containers/UserContainer';
import PlatformOrderingDashboardContent from './components/PlatformOrderingDashboardContent';
import PlatformSchedulingDashboardContent from './components/PlatformSchedulingDashboardContent';
import { FoodOrderingFeatureFlagId, GenAIFoodOrdering, HealthcareSchedulingFeatureFlagId, OutboundCallFeatureFlagId } from '../../../../shared/constants/featureFlagIds';
import { getApplicationsForOrg } from '../../../../shared/api/controllers/applications';
import ApplicationWithMembers from '../../../../shared/models/applications/api/ApplicationWithMembers';

const isFeatureAppExcluded = (app: ApplicationWithMembers) => {
    const appName = app?.name?.toLowerCase() || '';
    return appName.includes('manage menu') || appName.includes('manage entities') || appName.includes('shared topics');
};

const matchesPlatformVertical = (app: ApplicationWithMembers, vertical: 'ordering' | 'scheduling') => {
    if (isFeatureAppExcluded(app)) return false;

    const flags = app.featureFlags || [];
    if (vertical === 'ordering') {
        return flags.some(flag => flag.id === FoodOrderingFeatureFlagId || flag.id === GenAIFoodOrdering);
    }

    return flags.some(flag => flag.id === HealthcareSchedulingFeatureFlagId);
};

type PlatformRouteParams = {
    vertical: 'ordering' | 'scheduling';
};

interface PlatformDashboardOrg {
    id: string;
    name: string;
    imageUri?: string | null;
    isMultiLingual?: boolean;
    hasLiveApp: boolean;
    liveAppCount: number;
    matchingApps: ApplicationWithMembers[];
}

const PlatformDashboard: React.FC<RouteComponentProps<PlatformRouteParams>> = ({ match }) => {
    return (
        <Subscribe to={[OrganizationContainer, UserContainer]}>
            {(organizationContainer: OrganizationContainer, userContainer: UserContainer) => (
                <PlatformDashboardInner
                    vertical={match.params.vertical}
                    organizationContainer={organizationContainer}
                    userContainer={userContainer}
                />
            )}
        </Subscribe>
    );
};

interface PlatformDashboardInnerProps {
    vertical: 'ordering' | 'scheduling';
    organizationContainer: OrganizationContainer;
    userContainer: UserContainer;
}

const PlatformDashboardInner: React.FC<PlatformDashboardInnerProps> = ({
    vertical,
    organizationContainer,
    userContainer
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isCheckingUser, setIsCheckingUser] = useState(true);
    const [platformOrganizations, setPlatformOrganizations] = useState<PlatformDashboardOrg[]>([]);

    useEffect(() => {
        let cancelled = false;

        const ensureUser = async () => {
            try {
                if (!userContainer.state.currentUser) {
                    await userContainer.getUser();
                }
            } finally {
                if (!cancelled) {
                    setIsCheckingUser(false);
                }
            }
        };

        ensureUser();

        return () => {
            cancelled = true;
        };
    }, [userContainer]);

    const currentUserEmail = userContainer.state.currentUser?.email?.toLowerCase() || '';
    const isSystemUser = currentUserEmail.endsWith('@voicify.com');

    useEffect(() => {
        let isCancelled = false;

        const load = async () => {
            if (!isSystemUser) {
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            try {
                const orgResult = await organizationContainer.getOrganizations();
                const orgs = orgResult?.data || organizationContainer.state.organizations || [];

                const matchingOrgs = await Promise.all(orgs.map(async org => {
                    const appsResult = await getApplicationsForOrg(org.id);
                    const apps = (appsResult?.data || []) as ApplicationWithMembers[];
                    const filteredApps = apps.filter(app => matchesPlatformVertical(app, vertical));

                    if (filteredApps.length === 0) {
                        return null;
                    }

                    return {
                        id: org.id,
                        name: org.name,
                        imageUri: org.imageUri,
                        isMultiLingual: org.isMultiLingual,
                        hasLiveApp: filteredApps.some(app => app.isLive),
                        liveAppCount: filteredApps.filter(app => app.isLive).length,
                        matchingApps: filteredApps
                    } as PlatformDashboardOrg;
                }));

                if (!isCancelled) {
                    const liveOrganizations = matchingOrgs
                        .filter((org): org is PlatformDashboardOrg => Boolean(org && org.hasLiveApp))
                        .sort((a, b) => {
                            if (b.liveAppCount !== a.liveAppCount) {
                                return b.liveAppCount - a.liveAppCount;
                            }

                            return a.name.localeCompare(b.name);
                        });

                    setPlatformOrganizations(liveOrganizations);
                }
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            }
        };

        if (!isCheckingUser) {
            load();
        }

        return () => {
            isCancelled = true;
        };
    }, [vertical, isSystemUser, isCheckingUser, organizationContainer]);

    const entityNameMap = useMemo(() => {
        return platformOrganizations.reduce((acc, org) => {
            acc[org.id] = org.name;
            return acc;
        }, {} as Record<string, string>);
    }, [platformOrganizations]);

    const appNameMap = useMemo(() => {
        return platformOrganizations.reduce((acc, org) => {
            org.matchingApps.forEach(app => {
                acc[app.id] = `${org.name} - ${app.name}`;
            });
            return acc;
        }, {} as Record<string, string>);
    }, [platformOrganizations]);

    const representativeAppId = useMemo(() => {
        return platformOrganizations[0]?.matchingApps?.[0]?.id;
    }, [platformOrganizations]);

    const appIdsByOrganizationId = useMemo(() => {
        return platformOrganizations.reduce((acc, org) => {
            acc[org.id] = org.matchingApps.map(app => app.id);
            return acc;
        }, {} as Record<string, string[]>);
    }, [platformOrganizations]);

    const appLiveStatusMap = useMemo(() => {
        return platformOrganizations.reduce((acc, org) => {
            org.matchingApps.forEach(app => {
                acc[app.id] = app.isLive === true;
            });
            return acc;
        }, {} as Record<string, boolean>);
    }, [platformOrganizations]);

    const hasOutboundCallApps = useMemo(() => {
        return platformOrganizations.some(org =>
            org.matchingApps.some(app => app.featureFlags?.some(flag => flag.id === OutboundCallFeatureFlagId))
        );
    }, [platformOrganizations]);

    const organizationItems = useMemo(() => {
        return platformOrganizations.map(org => ({
            id: org.id,
            name: org.name,
            imageUri: org.imageUri,
            isMultiLingual: org.isMultiLingual,
            hasLiveApp: org.hasLiveApp,
            liveAppCount: org.liveAppCount,
            matchingAppCount: org.matchingApps.length,
            matchingAppIds: org.matchingApps.map(app => app.id),
            matchingAppLiveStatusMap: org.matchingApps.reduce((acc, app) => {
                acc[app.id] = app.isLive === true;
                return acc;
            }, {} as Record<string, boolean>)
        }));
    }, [platformOrganizations]);

    if (isCheckingUser) {
        return null;
    }

    if (!isSystemUser) {
        return <Redirect to="/v" />;
    }

    if (vertical === 'ordering') {
        return (
            <PlatformOrderingDashboardContent
                organizations={organizationItems}
                appNameMap={appNameMap}
                entityNameMap={entityNameMap}
                representativeAppId={representativeAppId}
                platformAppIdsByOrganizationId={appIdsByOrganizationId}
                selectedAppLiveStatusMap={appLiveStatusMap}
                isLoading={isLoading}
            />
        );
    }

    return (
        <PlatformSchedulingDashboardContent
            organizations={organizationItems}
            appNameMap={appNameMap}
            entityNameMap={entityNameMap}
            representativeAppId={representativeAppId}
            platformAppIdsByOrganizationId={appIdsByOrganizationId}
            selectedAppLiveStatusMap={appLiveStatusMap}
            hasOutboundCallApps={hasOutboundCallApps}
            isLoading={isLoading}
        />
    );
};

export default PlatformDashboard;
